import { Box, Grid, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { Base } from "../../base";
import Site from "../../../types/site";
import { useSelector } from "../../../store";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import { useNavigate } from "react-router-dom";
import { GridCellParams } from "@mui/x-data-grid";
import ColumnDef from "../../../types/columnDef";
import SettingsModal from "../../../types/settingsModal";

interface Props {
  apiGetSite: (id: number) => void;
  mobileRows: any[];
  next: () => void;
  apiSiteSearch: (search: SearchParams) => Promise<void>;
  setFilter: (params: SearchParams) => void;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  setHighlightId: React.Dispatch<React.SetStateAction<number>>;
  highlightId: number;
  onClickNewSite: () => void;
}

const SitesTable: React.FC<Props> = (props) => {
  const {
    apiGetSite,
    mobileRows,
    next,
    apiSiteSearch,
    setFilter,
    phone,
    setDeleteModal,
    setHighlightId,
    highlightId,
    onClickNewSite,
  } = props;
  const theme = useTheme();
  const { siteSearchResults, siteFilter, status } = useSelector(
    (store) => store.site,
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (phone) {
      next();
    }
    return () => {
      setFilter(defaultParams);
    };
  }, [phone]);

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "siteNumber",
      headerName: "Number",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "siteType",
      headerName: "Type",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((site: Site) => {
      let rowObject = {
        id: site.id,
        name: site.name,
        siteNumber: site.siteNumber || "Not Provided or N/A",
        siteType: site.siteType?.value,
        siteManager: site.siteManager?.value,
        actions: (
          <Base.TableActions
            id={site.id}
            handleView={() => {
              window.open(
                `/site/view/${site.id}`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
            handleEdit={() => {
              apiGetSite(site.id);
              navigate(`/site/edit/${site.id}`);
              setFilter(defaultParams);
            }}
            // TODO: Lock behind when we add roles
            handleDelete={() => {
              setDeleteModal({ visible: true, modal: "site" });
              apiGetSite(site.id);
              setHighlightId(site.id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const rows = displayRows(siteSearchResults?.results || []);

  const mobileDisplayRows = displayRows(mobileRows);

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiSiteSearch({
      ...siteFilter,
      pageNumber,
      pageSize,
    }).then(() => {
      setFilter({
        ...siteFilter,
        pageNumber,
        pageSize,
      });
    });
  };

  const onCellClick = (params: any) => {
    if (params.field === "name") {
      navigate(`/site/view/${params.id}`);
    }
  };

  const getOrderBy = (field: string) => {
    const fieldList = ["name", "siteType", "siteManager"];
    return fieldList.find((value) => value === field) ?? undefined;
  };

  return (
    <Box
      sx={{
        marginX: { sm: 5, xs: 0 },
        "& .link": {
          cursor: "pointer",
        },
        "& .header": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      }}
    >
      <Grid
        container
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={1}
        px={{ xs: 2, sm: 0 }}
      >
        <Grid
          sx={{
            display: { sm: "flex", xs: "none" },
          }}
        >
          <Base.Text text="Sites" variant="h3" />
        </Grid>
        <Base.Button
          text="New Site"
          onClick={onClickNewSite}
          fullWidth={phone}
        />
      </Grid>
      <Base.TableContainer
        columns={columns}
        rows={rows}
        mobileRows={mobileDisplayRows}
        page={siteFilter.pageNumber}
        pageSize={siteFilter.pageSize}
        totalRows={siteSearchResults?.totalResults}
        changePage={changePage}
        next={next}
        loading={status === "loading"}
        onCellClick={onCellClick}
        getCellClassName={(params: GridCellParams<any, any, any>) => {
          return params.field === "name" ? "link" : "";
        }}
        handleSort={(orderBy, orderDirection) => {
          const orderByField = orderBy ? getOrderBy(orderBy) : undefined;
          apiSiteSearch({
            ...siteFilter,
            orderBy: orderByField,
            orderDirection,
          });
        }}
        highlightId={highlightId}
      />
    </Box>
  );
};

export default SitesTable;
